import { UserContext } from '@/components/App';
import { carrierOrgClientIds, useCarrierOrgAggregate, useWeekStartsOn } from '@/hooks';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { FILTERS } from './constants';
import { addDays, addMonths, endOfWeek, format, getWeek, startOfWeek } from 'date-fns';
import { INVOICES, UPDATE_INVOICES } from './graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import * as ROUTES from '@/constants/routes';
import csvDownload from 'json-to-csv-export';
import { generateLineItems } from './utils';
import { quickbooksCsvColumns, useColumns } from './columns';
import { buildCustomRangeWeek, payPeriodWhere } from '@/utilities/payPeriod';

export const Context = createContext();

const dateNumeric = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

const TODAY = new Date(new Date().setHours(0, 0, 0, 0));

const PAGE_SIZE = 50;

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { client, admin_clients } = useContext(UserContext);
    const weekStartsOn = useWeekStartsOn();
    // Carrier-only org at "All Accounts": aggregate this page across the org's own accounts.
    const orgAggregate = useCarrierOrgAggregate();
    const [hasMore, setHasMore] = useState(false);
    const [selectedMap, setSelected] = useState({});
    const [filter, setFilter] = useState({
        status: FILTERS.NOT_PAID,
        carriers: [],
    });

    // Pay-period dropdown options: current week + 10 prior weeks, anchored to tenant week-start.
    // A separate "Custom Range" option is appended in the UI; selecting it opens a date picker
    // that produces a `filter.week` with `isCustom: true` and ad-hoc sow/eow.
    const weeks = useMemo(() => {
        const COUNT = 11; // current + 10 prior
        return Array(COUNT)
            .fill(0)
            .map((_, idx) => {
                const day = addDays(TODAY, -1 * idx * 7);
                const sow = startOfWeek(day, { weekStartsOn });
                const eow = endOfWeek(day, { weekStartsOn });
                return {
                    key: `week-${idx}`,
                    sow,
                    eow,
                    week_number: getWeek(day, { weekStartsOn }),
                    label:
                        idx === 0
                            ? `Current Week (${dateNumeric.format(sow)} - ${dateNumeric.format(eow)})`
                            : `${dateNumeric.format(sow)} - ${dateNumeric.format(eow)}`,
                };
            });
    }, [weekStartsOn]);

    // Apply a custom date range from the picker's "Custom Range" option.
    const setCustomRange = useCallback((sow, eow) => {
        const week = buildCustomRangeWeek(sow, eow);
        if (!week) return;
        setFilter((prev) => ({ ...prev, week }));
    }, []);

    const billTo = useMemo(() => {
        return [
            { value: 'ONWARD', label: 'Onward Marketplace' },
            { value: 'CUSTOMER', label: 'Customers' },
            { value: 'INTERNAL', label: 'Internal' },
        ];
    }, []);

    const [getInvoices, { data, loading: initInflight, fetchMore }] = useLazyQuery(INVOICES, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const [updateInvoices, { loading: updateInvoiceInflight }] = useMutation(UPDATE_INVOICES, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const where = useMemo(() => {
        // An ADMIN with no account selected has no `client`. Without this guard the client_id
        // branch below dereferences null and throws; returning {} makes the fetch effect skip.
        if (!orgAggregate && !client?.client_id) return {};

        // `client_id` is the PAYEE on every type listed here, so scoping to the org's own
        // accounts yields exactly the union of what each account sees on its own. Do NOT add
        // partner_client_id: on a RECEIVABLE that column holds the carrier, so matching it would
        // pull in shipper-facing invoices and leak what the shipper was charged.
        const scope = orgAggregate
            ? { client_id: { _in: carrierOrgClientIds(admin_clients) } }
            : { client_id: { _eq: client.client_id } };

        let where = [
            { type: { _in: ['ONWARD_PAYABLE', 'PAYABLE', 'INTERNAL'] } },
            scope,
            { status: { _nin: ['REVERTED'] } },
        ];

        switch (filter.status) {
            case FILTERS.DRAFT:
                where.push({ status: { _eq: 'DRAFT' } });
                break;
            case FILTERS.NOT_PAID:
                where.push({ status: { _eq: 'UNPAID' } });
                break;
            case FILTERS.APPROVED:
                where.push({ status: { _eq: 'APPROVED' } });
                break;
            case FILTERS.PAID:
                where.push({ status: { _eq: 'PAID' } });
                break;
        }

        if (filter?.billTo) {
            switch (filter.billTo) {
                case 'ONWARD':
                    where.push({ type: { _eq: 'ONWARD_PAYABLE' } });
                    break;
                case 'CUSTOMER':
                    where.push({ type: { _eq: 'PAYABLE' } });
                    break;
                case 'INTERNAL':
                    where.push({ type: { _eq: 'INTERNAL' } });
                    break;
            }
        }

        if (filter?.week) {
            where.push(...payPeriodWhere(filter.week));
        }

        if (filter?.searchTerm) {
            const orConditions = [
                { orders: { order_number: { _ilike: `%${filter.searchTerm}%` } } },
                { orders: { po_number: { _ilike: `%${filter.searchTerm}%` } } },
            ];

            const parsedInt = parseInt(filter.searchTerm, 10);
            if (!isNaN(parsedInt) && Number.isInteger(parsedInt)) {
                orConditions.push({ invoice_number: { _eq: parsedInt } });
            }

            where.push({ _or: orConditions });
        }

        return { _and: where };
    }, [client, filter, orgAggregate, admin_clients]);

    useEffect(() => {
        if (Object.keys(where).length > 0) {
            getInvoices({
                variables: {
                    where,
                    limit: PAGE_SIZE,
                    offset: 0,
                },
            }).then(({ data }) => {
                setHasMore(data?.results?.length === PAGE_SIZE);
            });
        }
    }, [where, PAGE_SIZE]);

    const invoices = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const selected = useMemo(() => {
        return Object.keys(selectedMap).filter((attr) => selectedMap[attr]);
    }, [selectedMap]);

    const invoicesMap = useMemo(() => {
        return Object.fromEntries(invoices.map((invoice) => [invoice.carrier_invoice_id, invoice]));
    }, [invoices]);

    const selectedObj = useMemo(() => {
        return selected.map((id) => invoicesMap[id]);
    }, [selected, invoicesMap]);

    const loadMore = useCallback(() => {
        fetchMore({
            variables: {
                where,
                limit: PAGE_SIZE,
                offset: data?.results?.length || 0,
            },
            updateQuery: (data, { fetchMoreResult }) => {
                const prev = Object.fromEntries(data.results.map((invoice) => [invoice.carrier_invoice_id, true]));
                const clone = [
                    ...data.results,
                    ...fetchMoreResult.results.filter((invoice) => !prev[invoice.carrier_invoice_id]),
                ];
                return {
                    results: clone,
                };
            },
        }).then((result) => {
            if (result.data.results.length < PAGE_SIZE) {
                setHasMore(false);
            }
        });
    }, [where, fetchMore, PAGE_SIZE, data]);

    const callbacks = {
        exportCsv: () => {
            const exportData = generateLineItems(selectedObj);
            // Org-level exports span several accounts, which are separate QuickBooks vendors —
            // prepend the payee account so the rows stay attributable. Per-account exports keep
            // their existing column set and order.
            const csvColumns = quickbooksCsvColumns(orgAggregate);
            csvDownload({
                headers: csvColumns.map((col) => col.header),
                filename: `invoices-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    csvColumns.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
        setFilter,
        loadMore,
        onRowClick: (invoice) => {
            const path = ROUTES.ACCOUNTING_INVOICE.replace(':invoice_id', invoice.carrier_invoice_id);
            return navigate(path);
        },
        getRowId: (invoice) => {
            return invoice.carrier_invoice_id;
        },
        setSubmitted: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'UNPAID',
                    },
                },
            });
        },
        setApproved: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'APPROVED',
                    },
                },
            });
        },
        setPaid: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'PAID',
                    },
                },
            });
        },
        submitId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'UNPAID',
                    },
                },
            });
        },
        approveId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'APPROVED',
                    },
                },
            });
        },
        payId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'PAID',
                    },
                },
            });
        },
        cancelId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'CANCELLED',
                    },
                },
            });
        },
        flagRowToggle: (invoice) => {
            updateInvoices({
                variables: {
                    ids: [invoice.carrier_invoice_id],
                    patch: {
                        flagged: !invoice.flagged,
                    },
                },
            });
        },
        selectRows: setSelected,
        setCustomRange,
    };

    const COLUMNS = useColumns({ callbacks, includeAccount: orgAggregate });

    return (
        <Context.Provider
            value={{
                state: {
                    weeks,
                    hasMore,
                    selected,
                    selectedObj,
                    invoices,
                    billTo,
                    filter,
                    columns: COLUMNS,
                },
                loading: {
                    init: initInflight,
                    update: updateInvoiceInflight,
                    create: false,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
